import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** Card · FramedWindow · SectionHeader · EmptyState · PieceCard — §2, §5, §6, addendum §10. */
import { Link as AriaLink } from 'react-aria-components';
import { StatusBadge } from './Status.js';
/** A sheet resting on the bg: borderless, one soft shadow (light) / lighter (dark). */
export function Card({ raised = false, children, ...rest }) {
    return (_jsx("div", { ...rest, className: raised ? 'card card-raised' : 'card', children: children }));
}
export function FramedWindow({ caption, captionEnd, aspectRatio = '4 / 3', state = 'filled', locked = false, mark, pad, children, }) {
    const inner = [
        'window-in',
        state === 'empty' ? 'is-empty' : null,
        state === 'making' ? 'is-making' : null,
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsxs("div", { className: "window", style: pad === undefined ? undefined : { padding: pad }, children: [_jsxs("div", { className: inner, style: {
                    aspectRatio,
                    ...(locked ? { boxShadow: 'inset 0 0 0 2px var(--primary)' } : {}),
                }, children: [children, mark !== undefined && _jsx("span", { className: "ph-mark", children: mark })] }), (caption !== undefined || captionEnd !== undefined) && (_jsxs("div", { className: "window-cap", children: [_jsx("span", { children: caption }), _jsx("span", { children: captionEnd })] }))] }));
}
/** The panel header: a title and a mono meta, over a `--line` rule. */
export function SectionHeader({ title, meta }) {
    return (_jsxs("div", { className: "panelhead", children: [_jsx("span", { className: "ph-t", children: title }), meta !== undefined && _jsx("span", { className: "ph-c", children: meta })] }));
}
/** The head of a working surface — not the top bar (addendum §10). */
export function SurfaceHeader({ kicker, title, meta, actions }) {
    return (_jsxs("div", { className: "surfhead", children: [_jsxs("div", { children: [kicker !== undefined && _jsx("span", { className: "kicker", children: kicker }), _jsx("h2", { className: "ui-h sh-t", children: title }), meta !== undefined && _jsx("span", { className: "sh-meta", children: meta })] }), actions !== undefined && _jsx("div", { className: "sh-acts", children: actions })] }));
}
/**
 * "A thing you haven't needed yet" — never a failure.
 *
 * `h3`, the same level a piece card's name takes: an empty state sits where those cards would
 * have, under the surface's `h2`, and an `h4` there left a hole in the outline.
 */
export function EmptyState({ title, body, actions }) {
    return (_jsxs("div", { className: "empty", children: [_jsx("h3", { children: title }), body !== undefined && _jsx("p", { children: body }), actions !== undefined && _jsx("div", { className: "btnrow", children: actions })] }));
}
/**
 * The Library unit.
 *
 * The card **states its own status and nothing aggregates it** — there is no "N of M ready"
 * anywhere, because a library is not a checklist. An errored piece offers a retry rather
 * than an apology, and an unmade one is an invitation.
 */
export function PieceCard({ kind, name, href, note, status, statusLabel, thumb = 'filled', thumbnail, thumbLabel, action, archived = false, chosen = false, }) {
    const classes = ['piece', archived ? 'is-archived' : null, chosen ? 'is-chosen' : null]
        .filter(Boolean)
        .join(' ');
    const thumbClass = thumb === 'filled'
        ? 'p-thumb'
        : thumb === 'making'
            ? 'p-thumb is-making'
            : thumb === 'error'
                ? 'p-thumb is-error'
                : 'p-thumb is-empty';
    return (_jsxs("article", { className: classes, children: [_jsx("div", { className: thumbClass, children: thumb === 'filled' ? thumbnail : _jsx("span", { children: thumbLabel }) }), _jsxs("div", { className: "p-body", children: [_jsx("span", { className: "p-kind", children: kind }), _jsx("h3", { children: href === undefined ? name : _jsx(AriaLink, { href: href, children: name }) }), note !== undefined && _jsx("p", { className: "p-note", children: note }), _jsxs("div", { className: "p-foot", children: [_jsx(StatusBadge, { status: status, children: statusLabel }), action !== undefined && _jsx("span", { children: action })] })] })] }));
}
//# sourceMappingURL=Surfaces.js.map